import { Button, Group } from '@mantine/core';
import { IconDownload, IconPrinter } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zonedStamp } from '../../inputs/lt-utc.js';
import classes from './DrillRecords.module.css';
import type { DrillRecordSource } from './record-source.js';
import type { DrillAuditEntryView, DrillRecordDetail } from './record-types.js';
import { useDrillReportExport } from './report-export.js';

const TONE: Partial<Record<DrillAuditEntryView['kind'], 'decision' | 'warning'>> = {
  approved: 'decision',
  returned: 'warning',
  reopened: 'warning',
  stopped: 'warning',
  cancelled: 'warning',
};

/**
 * Denetim izi (ortağın belgesi §13.6; ADR 0128 Faz 5 Ek): kaydın oluşturulması, düzenlemeleri
 * (bölüm ve alan sayısıyla), gönderimi, kaptan kararı, yeniden açılması ve bulguları; zaman gemi
 * saati (LT) ve UTC. Kaynak değiştirilemez olay tabloları ve `audit_log`'dur; ekran yalnız okur.
 */
export function DrillAuditTrail({ record }: { record: DrillRecordDetail }) {
  const { t } = useTranslation();
  const a = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.audit.${key}`, values);
  const trail = record.auditTrail ?? [];
  return (
    <section aria-labelledby={`audit-${record.id}`}>
      <h4 id={`audit-${record.id}`} className={classes.sectionTitle}>
        {a('title')}
      </h4>
      <p className={classes.hint}>{a('hint')}</p>
      {trail.length === 0 ? (
        <p className={classes.muted}>{a('empty')}</p>
      ) : (
        <ol className={classes.trail}>
          {trail.map((entry) => {
            const detail = [
              entry.sections.length > 0
                ? entry.sections.map((s) => a(`sections.${s}`)).join(', ')
                : null,
              entry.kind === 'edited' && entry.fields > 0
                ? a('fields', { count: entry.fields })
                : null,
              entry.labels.length > 0 ? entry.labels.join(', ') : null,
            ].filter(Boolean);
            return (
              <li
                key={`${entry.kind}-${entry.at}-${entry.by}`}
                className={classes.trailItem}
                data-tone={TONE[entry.kind]}
              >
                <span className={classes.trailHead}>
                  <span className={classes.trailKind}>{a(`kinds.${entry.kind}`)}</span>
                  <span className={classes.trailMeta}>
                    {`${zonedStamp(entry.at, record.tzOffsetMin)} LT · ${zonedStamp(entry.at)} UTC`}
                  </span>
                </span>
                <span className={classes.trailMeta}>
                  {entry.byName ?? '—'}
                  {entry.capacity === 'deputy' ? ` (${a('onBehalf')})` : ''}
                </span>
                {detail.length > 0 ? (
                  <span className={classes.trailDetail}>{detail.join(' · ')}</span>
                ) : null}
                {entry.reason ? <span className={classes.trailDetail}>{entry.reason}</span> : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Başlıktaki rapor düğmeleri: Part A–E PDF'ini indir ya da yazdır (Faz 5 Ek). */
export function DrillReportButtons({
  record,
  source,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
}) {
  const { t } = useTranslation();
  const { busy, single } = useDrillReportExport(source);
  const [error, setError] = useState<string | null>(null);
  const run = async (mode: 'save' | 'print') => {
    setError(null);
    setError(await single(record, mode));
  };
  return (
    <>
      <Group gap={8} wrap="wrap">
        <Button
          variant="default"
          leftSection={<IconDownload size={16} aria-hidden />}
          loading={busy !== null}
          onClick={() => void run('save')}
        >
          {t('drillReport.actions.pdf')}
        </Button>
        <Button
          variant="default"
          leftSection={<IconPrinter size={16} aria-hidden />}
          disabled={busy !== null}
          onClick={() => void run('print')}
        >
          {t('drillReport.actions.print')}
        </Button>
      </Group>
      {error ? (
        <span role="alert" className={classes.exportError}>
          {error}
        </span>
      ) : null}
    </>
  );
}
